export type Language = 'ro' | 'en' | 'fr';

export interface HomeTranslationSet {
  home: Record<string, string>;
  footer: Record<string, string>;
}

let translationsPromise: Promise<Record<Language, HomeTranslationSet>> | null = null;
function loadHomeTranslations(): Promise<Record<Language, HomeTranslationSet>> {
  if (!translationsPromise) {
    translationsPromise = import('./home-translations').then(m => m.homeTranslations);
  }
  return translationsPromise;
}

const FLAG_SVGS: Record<Language, string> = {
  ro: '<svg viewBox="0 0 3 2"><rect width="1" height="2" fill="#002B7F"/><rect x="1" width="1" height="2" fill="#FCD116"/><rect x="2" width="1" height="2" fill="#CE1126"/></svg>',
  en: '<svg viewBox="0 0 60 30"><rect width="60" height="30" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="2"/><path d="M0,15 H60 M30,0 V30" stroke="#fff" stroke-width="10"/><path d="M0,15 H60 M30,0 V30" stroke="#C8102E" stroke-width="6"/></svg>',
  fr: '<svg viewBox="0 0 3 2"><rect width="1" height="2" fill="#002395"/><rect x="1" width="1" height="2" fill="#FFFFFF"/><rect x="2" width="1" height="2" fill="#ED2939"/></svg>'
};

function getSavedLanguage(): Language {
  try {
    const saved = localStorage.getItem('language');
    if (saved === 'ro' || saved === 'en' || saved === 'fr') return saved;
    // Check cookie fallback
    const match = /(?:^|;\s*)language=(ro|en|fr)(?:;|$)/.exec(document.cookie);
    if (match && (match[1] === 'ro' || match[1] === 'en' || match[1] === 'fr')) {
      return match[1] as Language;
    }
  } catch {
    // Ignore storage errors
  }
  return 'ro';
}

function resolveTranslationFromDict(dict: Record<Language, HomeTranslationSet>, lang: Language, keyPath: string): string | undefined {
  const parts = keyPath.split('.');
  
  // 1. Check in specified language
  let current: unknown = dict[lang];
  for (const part of parts) {
    if (current === undefined || current === null) {
      current = undefined;
      break;
    }
    current = typeof current === 'object' ? (current as Record<string, unknown>)[part] : undefined;
  }
  if (typeof current === 'string') return current;

  // 2. Fallback to Romanian
  let roCurrent: unknown = dict['ro'];
  for (const part of parts) {
    if (roCurrent === undefined || roCurrent === null) {
      roCurrent = undefined;
      break;
    }
    roCurrent = typeof roCurrent === 'object' ? (roCurrent as Record<string, unknown>)[part] : undefined;
  }
  return typeof roCurrent === 'string' ? roCurrent : undefined;
}

export function setupHomeLanguage() {
  const langControl = document.getElementById('home-lang-control');
  const langBtn = document.getElementById('home-lang-btn');
  const dropdown = document.getElementById('home-lang-dropdown');
  const codeSpan = document.getElementById('home-lang-code-current');
  const flagSpan = document.getElementById('home-lang-flag-current');
  const chevron = langBtn?.querySelector('.home-lang-chevron');

  if (!langControl || !langBtn || !dropdown) return () => {};

  let currentLang: Language = getSavedLanguage();

  const applyLanguage = async (lang: Language, persist = true) => {
    currentLang = lang;
    document.documentElement.lang = lang;

    if (persist) {
      try {
        localStorage.setItem('language', lang);
        document.cookie = `language=${lang};path=/;max-age=31536000;SameSite=Lax`;
        window.dispatchEvent(new CustomEvent('app:language-change', { detail: lang }));
      } catch {
        // Ignore storage errors
      }
    }

    if (codeSpan) codeSpan.textContent = lang.toUpperCase();
    if (flagSpan) flagSpan.innerHTML = FLAG_SVGS[lang];

    dropdown.querySelectorAll<HTMLButtonElement>('.home-lang-option').forEach(opt => {
      const isSelected = opt.dataset.lang === lang;
      opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    // If Romanian on initial load and no translations were fetched yet,
    // the static HTML is already Romanian.
    if (lang === 'ro' && !translationsPromise) {
      return;
    }

    const dict = await loadHomeTranslations();

    // Translate DOM elements marked with data-i18n
    document.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
      const key = el.dataset.i18n;
      if (!key) return;
      const translated = resolveTranslationFromDict(dict, lang, key);
      if (translated !== undefined) {
        if (el.hasAttribute('data-i18n-html') || translated.includes('<')) {
          el.innerHTML = translated;
        } else {
          el.textContent = translated;
        }
      }
    });

    // Translate DOM attributes marked with data-i18n-aria-label
    document.querySelectorAll<HTMLElement>('[data-i18n-aria-label]').forEach(el => {
      const key = el.getAttribute('data-i18n-aria-label');
      if (!key) return;
      const translated = resolveTranslationFromDict(dict, lang, key);
      if (translated !== undefined) {
        el.setAttribute('aria-label', translated);
      }
    });
  };

  const openDropdown = () => {
    dropdown.hidden = false;
    langBtn.setAttribute('aria-expanded', 'true');
    chevron?.classList.add('open');
  };

  const closeDropdown = () => {
    dropdown.hidden = true;
    langBtn.setAttribute('aria-expanded', 'false');
    chevron?.classList.remove('open');
  };

  const toggleDropdown = () => {
    if (dropdown.hidden) {
      openDropdown();
    } else {
      closeDropdown();
    }
  };

  const onBtnClick = (e: MouseEvent) => {
    e.stopPropagation();
    toggleDropdown();
  };

  const onDocClick = (e: MouseEvent) => {
    if (!langControl.contains(e.target as Node)) {
      closeDropdown();
    }
  };

  const onDocKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeDropdown();
      langBtn.focus();
    }
  };

  const onOptionClick = (e: Event) => {
    const target = (e.target as Element).closest<HTMLButtonElement>('.home-lang-option');
    if (!target) return;
    const choice = target.dataset.lang as Language;
    if (choice === 'ro' || choice === 'en' || choice === 'fr') {
      void applyLanguage(choice, true);
      closeDropdown();
      langBtn.focus();
    }
  };

  const onStorage = (e: StorageEvent) => {
    if (e.key === 'language' && e.newValue && (e.newValue === 'ro' || e.newValue === 'en' || e.newValue === 'fr')) {
      void applyLanguage(e.newValue as Language, false);
    }
  };

  const onCustomLanguageChange = (e: Event) => {
    const custom = e as CustomEvent<Language>;
    if (custom.detail && (custom.detail === 'ro' || custom.detail === 'en' || custom.detail === 'fr')) {
      if (custom.detail !== currentLang) {
        void applyLanguage(custom.detail, false);
      }
    }
  };

  langBtn.addEventListener('click', onBtnClick);
  dropdown.addEventListener('click', onOptionClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeyDown);
  window.addEventListener('storage', onStorage);
  window.addEventListener('app:language-change', onCustomLanguageChange);

  // Initial apply
  void applyLanguage(currentLang, false);

  return () => {
    langBtn.removeEventListener('click', onBtnClick);
    dropdown.removeEventListener('click', onOptionClick);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onDocKeyDown);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('app:language-change', onCustomLanguageChange);
  };
}
